import React, { Component } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { Button, Dimmer, Icon, Loader, Menu, Modal, Popup, Table } from 'semantic-ui-react';
import { connect } from 'redux-scaffolding-ts';
import { resolve } from 'inversify.config';
import { IdentityService } from 'services/identity-service';
import { EventTypesStore } from 'stores/configuration/events-workflow/event-types-store';
import {
  EventTypeChecklistTemplateDto,
  EventTypeChecklistTemplateStore,
  UpsertEventTypeChecklistTemplateDto
} from 'stores/events/event-type-checklist-template-store';
import { isValidChecklistExcelFile, uploadEventTypeChecklistTemplate } from 'stores/configuration/events-n-requests/checklists-store';
import { ToastComponent } from 'site/pages/landing-pages/util/toast-component';

const TEMPLATE_EVENT_TYPES = ['TECHNICAL SUPPORT', 'TECHNICAL TRAINING', 'CONVERSION', 'RELOCATION'];

interface Props extends WithTranslation {
  eventTypesStore?: EventTypesStore;
  templateStore?: EventTypeChecklistTemplateStore;
}

interface Row {
  eventTypeId: string;
  eventTypeName: string;
  template: EventTypeChecklistTemplateDto | null;
  pendingTemplate?: UpsertEventTypeChecklistTemplateDto | null;
}

interface State {
  rows: Row[];
  loading: boolean;
  targetEventTypeId: string | null;
  confirmationEventTypeId: string | null;
  actionsMenuEventTypeId: string | null;
}

@connect(['eventTypesStore', EventTypesStore], ['templateStore', EventTypeChecklistTemplateStore])
class EventChecklistTemplateList extends Component<Props, State> {
  state: State = {
    rows: [],
    loading: false,
    targetEventTypeId: null,
    confirmationEventTypeId: null,
    actionsMenuEventTypeId: null
  };
  private fileInput = React.createRef<HTMLInputElement>();

  @resolve(IdentityService)
  private identityService: IdentityService;

  componentDidMount() {
    this.load();
  }

  private async load() {
    this.setState({ loading: true });
    const result = await this.props.eventTypesStore.getAllAsync({ searchQuery: '', orderBy: [], skip: 0, take: 1000 });
    const eventTypes = (result?.items || []).filter(
      type => type.active && TEMPLATE_EVENT_TYPES.includes((type.name || '').trim().toUpperCase())
    );
    // A missing template makes the endpoint fail; the store maps that to null.
    const rows = await Promise.all(
      eventTypes.map(async type => ({
        eventTypeId: type.id,
        eventTypeName: type.name,
        template: (await this.props.templateStore.getByEventTypeId(type.id)) ?? null
      }))
    );
    this.setState({ rows, loading: false });
  }

  private download = async (row: Row) => {
    try {
      await this.props.templateStore.downloadByEventTypeId(row.eventTypeId, row.template?.originalName);
    } catch {
      ToastComponent({ text: this.props.t('File download failed'), type: 'error-toast' });
    }
  };

  private startReplace = (eventTypeId: string) => {
    this.setState({ targetEventTypeId: eventTypeId }, () => this.fileInput.current?.click());
  };

  private onFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { t } = this.props;
    const file = e.target.files?.[0];
    const row = this.state.rows.find(r => r.eventTypeId === this.state.targetEventTypeId);
    e.target.value = '';
    if (!file || !row) return;
    if (!isValidChecklistExcelFile(file)) {
      ToastComponent({ text: t('Only Excel files (.xlsx, .xls) up to 5MB are allowed'), type: 'warning-toast' });
      return;
    }

    this.setState({ loading: true });
    try {
      const user = this.identityService.getUserInfo();
      const upload = await uploadEventTypeChecklistTemplate(file, row.eventTypeName, user.activeRole, user.userName);
      if (!upload.data.isSuccess || !upload.data.item?.uploadedFileName) throw new Error('upload');

      this.setState(state => ({
        rows: state.rows.map(item =>
          item.eventTypeId === row.eventTypeId
            ? {
                ...item,
                pendingTemplate: {
                  eventTypeId: row.eventTypeId,
                  originalName: file.name,
                  uploadedName: upload.data.item.uploadedFileName
                }
              }
            : item
        ),
        confirmationEventTypeId: row.eventTypeId,
        loading: false
      }));
    } catch {
      this.setState({ loading: false });
      ToastComponent({ text: t('File upload failed'), type: 'error-toast' });
    }
  };

  private remove = (row: Row) => {
    if (this.state.loading || !row.template) return;
    this.setState(state => ({
      rows: state.rows.map(item => (item.eventTypeId === row.eventTypeId ? { ...item, pendingTemplate: null } : item)),
      confirmationEventTypeId: row.eventTypeId
    }));
  };

  private closeConfirmation = () => {
    if (this.state.loading) return;
    this.setState(state => ({
      rows: state.rows.map(item => (item.eventTypeId === state.confirmationEventTypeId ? { ...item, pendingTemplate: undefined } : item)),
      confirmationEventTypeId: null
    }));
  };

  private confirmSave = () => {
    const row = this.state.rows.find(item => item.eventTypeId === this.state.confirmationEventTypeId);
    if (row) this.save(row);
  };

  private save = async (row: Row) => {
    const { t, templateStore } = this.props;
    if (this.state.loading || row.pendingTemplate === undefined) return;

    this.setState({ loading: true });
    try {
      const result = row.pendingTemplate
        ? await templateStore.saveAsync(row.pendingTemplate as EventTypeChecklistTemplateDto, 'Changed')
        : await templateStore.deleteAsync(row.eventTypeId);
      if (!result?.isSuccess) throw new Error('save');

      const template = row.pendingTemplate ? ({ ...row.template, ...row.pendingTemplate } as EventTypeChecklistTemplateDto) : null;
      this.setState(state => ({
        rows: state.rows.map(item => (item.eventTypeId === row.eventTypeId ? { ...item, template, pendingTemplate: undefined } : item)),
        confirmationEventTypeId: null
      }));
      if (template) {
        const confirmedTemplate = await templateStore.getByEventTypeId(row.eventTypeId);
        if (confirmedTemplate) {
          this.setState(state => ({
            rows: state.rows.map(item => (item.eventTypeId === row.eventTypeId ? { ...item, template: confirmedTemplate } : item))
          }));
        }
      }
      ToastComponent({ text: t('Files saved successfully!'), type: 'success-toast' });
    } catch {
      ToastComponent({ text: t('File save failed'), type: 'error-toast' });
    } finally {
      this.setState({ loading: false });
    }
  };

  render() {
    const { t } = this.props;
    const confirmationRow = this.state.rows.find(row => row.eventTypeId === this.state.confirmationEventTypeId);
    return (
      <>
        <Dimmer active={this.state.loading} inverted>
          <Loader />
        </Dimmer>
        <Table className="custom-table" selectable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>{t('Event Type')}</Table.HeaderCell>
              <Table.HeaderCell>{t('Template')}</Table.HeaderCell>
              <Table.HeaderCell>{t('Last modified')}</Table.HeaderCell>
              <Table.HeaderCell>{t('Modified by')}</Table.HeaderCell>
              <Table.HeaderCell />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {this.state.rows.map(row => (
              <Table.Row key={row.eventTypeId} warning={row.pendingTemplate !== undefined}>
                <Table.Cell>{row.eventTypeName}</Table.Cell>
                <Table.Cell>
                  {row.pendingTemplate === null
                    ? `${row.template?.originalName || ''} (${t('Pending deletion')})`
                    : (row.pendingTemplate || row.template)?.originalName ?? t('No template')}
                </Table.Cell>
                <Table.Cell>{row.template?.modifiedOn ? new Date(row.template.modifiedOn).toLocaleString() : ''}</Table.Cell>
                <Table.Cell>{row.template?.modifiedByUser ?? ''}</Table.Cell>
                <Table.Cell textAlign="right" className="no-left-border">
                  <Popup
                    on="click"
                    position="bottom right"
                    disabled={this.state.loading}
                    open={this.state.actionsMenuEventTypeId === row.eventTypeId}
                    onOpen={() => this.setState({ actionsMenuEventTypeId: row.eventTypeId })}
                    onClose={() => this.setState({ actionsMenuEventTypeId: null })}
                    trigger={
                      <Icon
                        name="ellipsis vertical"
                        className="clickable-icon"
                        style={{ fontSize: '15px', color: this.state.loading ? '#EEEEEE' : '#474b4f' }}
                        role="button"
                        tabIndex={this.state.loading ? -1 : 0}
                        title={t('Actions')}
                        aria-label={t('Actions')}
                        aria-expanded={this.state.actionsMenuEventTypeId === row.eventTypeId}
                        aria-disabled={this.state.loading}
                        onKeyDown={event => {
                          if (!this.state.loading && (event.key === 'Enter' || event.key === ' ')) {
                            event.preventDefault();
                            this.setState({
                              actionsMenuEventTypeId: this.state.actionsMenuEventTypeId === row.eventTypeId ? null : row.eventTypeId
                            });
                          }
                        }}
                      />
                    }
                  >
                    <Menu vertical secondary onClick={() => this.setState({ actionsMenuEventTypeId: null })}>
                      {row.template && row.pendingTemplate === undefined && (
                        <Menu.Item disabled={this.state.loading} onClick={() => this.download(row)}>
                          <Icon name="download" style={{ color: '#474b4f' }} />
                          {t('Download')}
                        </Menu.Item>
                      )}
                      <Menu.Item disabled={this.state.loading} onClick={() => this.startReplace(row.eventTypeId)}>
                        <Icon name="upload" style={{ color: '#474b4f' }} />
                        {row.template ? t('Replace') : t('Upload file')}
                      </Menu.Item>
                      {(row.pendingTemplate || (row.pendingTemplate === undefined && row.template)) && (
                        <Menu.Item disabled={this.state.loading} onClick={() => this.remove(row)}>
                          <span className="text__red text__bold">{t('Delete')}</span>
                          <Icon color="red" name="trash alternate" />
                        </Menu.Item>
                      )}
                    </Menu>
                  </Popup>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
        <Modal
          open={this.state.confirmationEventTypeId !== null}
          size="mini"
          className="have-warnings-popup"
          closeOnEscape={!this.state.loading}
          closeOnDimmerClick={!this.state.loading}
          onClose={this.closeConfirmation}
        >
          <Modal.Content className="have-warnings-popup__content">
            <p>
              {confirmationRow?.pendingTemplate === null
                ? t('Are you sure that you want to delete the item?')
                : t('Are you sure you want to save this template?')}
            </p>
            {confirmationRow?.pendingTemplate && <p>{confirmationRow.pendingTemplate.originalName}</p>}
          </Modal.Content>
          <div className="have-warnings-popup__buttons-container">
            <Button
              type="button"
              className="have-warnings-popup__btn"
              content={t('No')}
              disabled={this.state.loading}
              onClick={this.closeConfirmation}
            />
            <Button
              type="button"
              className="have-warnings-popup__btn have-warnings-popup__pending-btn"
              content={t('Yes')}
              disabled={this.state.loading}
              loading={this.state.loading}
              onClick={this.confirmSave}
            />
          </div>
        </Modal>
        <input hidden ref={this.fileInput} type="file" accept=".xlsx,.xls" onChange={this.onFileSelected} />
      </>
    );
  }
}

export default withTranslation()(EventChecklistTemplateList);
